/**
 * FighterSheet component - iOS-style modal for mobile fighter details
 */
import React, { useRef, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, CaretLeft, CaretRight } from 'phosphor-react';
import FighterComparison from './FighterComparison';
import ClickableElement from './ClickableElement';
import { useFightData } from '../contexts/FightDataContext';

interface FighterSheetProps {
  isOpen: boolean;
  onClose: () => void;
}

const FighterSheet: React.FC<FighterSheetProps> = ({ isOpen, onClose }) => {
  const { selectedFightIndex, setSelectedFightIndex, fightPredictions } = useFightData();
  const sheetRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const handleRef = useRef<HTMLDivElement>(null);
  const [startY, setStartY] = useState<number | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [translateY, setTranslateY] = useState(100); // Start offscreen (100%)
  const [isVisible, setIsVisible] = useState(false);
  
  // Get the current fight data
  const fight = fightPredictions[selectedFightIndex];
  
  // Navigation handlers
  const navigateToPrevFighter = () => {
    if (selectedFightIndex > 0) {
      setSelectedFightIndex(selectedFightIndex - 1);
      // Reset scroll position
      if (contentRef.current) {
        contentRef.current.scrollTop = 0;
      }
    }
  };

  const navigateToNextFighter = () => {
    if (selectedFightIndex < fightPredictions.length - 1) {
      setSelectedFightIndex(selectedFightIndex + 1);
      // Reset scroll position
      if (contentRef.current) {
        contentRef.current.scrollTop = 0;
      }
    }
  };
  
  // Handle animation when opening/closing
  useEffect(() => {
    if (isOpen) {
      // First make the element visible
      setIsVisible(true);
      // Then animate it in on the next frame (for proper transition)
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setTranslateY(0);
        });
      });
    } else {
      // Start close animation
      setTranslateY(100);
      
      // Keep element in DOM until animation completes
      const timer = setTimeout(() => {
        setIsVisible(false);
      }, 250); // Reduced from 350ms to 250ms for snappier feel
      return () => clearTimeout(timer);
    }
  }, [isOpen]);
  
  // Set up non-passive event listeners
  useEffect(() => {
    const handleElement = handleRef.current;
    const contentElement = contentRef.current;
    
    if (!handleElement || !contentElement) return;
    
    const handleTouchStartEvent = (e: TouchEvent) => {
      // Only initiate drag from the drag handle or if at the top of the content
      if (contentElement.scrollTop <= 0) {
        setStartY(e.touches[0].clientY);
      }
    };
    
    const handleTouchMoveEvent = (e: TouchEvent) => {
      if (startY !== null) {
        const currentY = e.touches[0].clientY;
        const deltaY = currentY - startY;
        
        // Only allow dragging downward
        if (deltaY > 0) {
          setIsDragging(true);
          // Set the translateY based on the drag distance with less resistance
          setTranslateY((deltaY / window.innerHeight) * 100 * 0.8); // Increased from 0.5 to 0.8 for more responsive feel
          e.preventDefault(); // Prevent scroll while dragging
        }
      }
    };
    
    const handleTouchEndEvent = () => {
      if (isDragging) {
        // If dragged more than 15% down, close the sheet (reduced from 20%)
        if (translateY > 15) {
          onClose();
        } else {
          // Otherwise, snap back to open position
          setTranslateY(0);
        }
        setIsDragging(false);
      }
      setStartY(null);
    };
    
    // Add event listeners with passive: false
    handleElement.addEventListener('touchstart', handleTouchStartEvent, { passive: false });
    handleElement.addEventListener('touchmove', handleTouchMoveEvent, { passive: false });
    handleElement.addEventListener('touchend', handleTouchEndEvent);
    
    // Add additional listeners for the content area top portion
    contentElement.addEventListener('touchstart', handleTouchStartEvent, { passive: false });
    contentElement.addEventListener('touchmove', handleTouchMoveEvent, { passive: false });
    contentElement.addEventListener('touchend', handleTouchEndEvent);
    
    return () => {
      // Remove all event listeners on cleanup
      handleElement.removeEventListener('touchstart', handleTouchStartEvent);
      handleElement.removeEventListener('touchmove', handleTouchMoveEvent);
      handleElement.removeEventListener('touchend', handleTouchEndEvent);
      
      contentElement.removeEventListener('touchstart', handleTouchStartEvent);
      contentElement.removeEventListener('touchmove', handleTouchMoveEvent);
      contentElement.removeEventListener('touchend', handleTouchEndEvent);
    };
  }, [isDragging, onClose, startY, translateY]);
  
  // Close when clicking on the backdrop (scrim)
  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === sheetRef.current) {
      onClose();
    }
  };
  
  // Add/remove body scroll lock when sheet is open
  useEffect(() => {
    const body = document.body;
    if (isOpen) {
      body.style.overflow = 'hidden';
    } else {
      body.style.overflow = '';
    }
    return () => {
      body.style.overflow = '';
    };
  }, [isOpen]);
  
  // If not open and not visible, don't render
  if (!isVisible) return null;
  
  const modalContent = (
    <div 
      ref={sheetRef}
      className="fixed inset-0 z-[9999] sheet-transition"
      style={{ 
        backgroundColor: 'rgba(0, 0, 0, 0.75)',
        opacity: translateY < 50 ? 1 : 1 - (translateY - 50) / 50
      }}
      onClick={handleBackdropClick}
    >
      <div 
        className="absolute bottom-0 left-0 right-0 shadow-lg overflow-hidden sheet-transition"
        style={{ 
          transform: `translateY(${translateY}%)`,
          height: 'calc(96% - env(safe-area-inset-bottom))',
          maxHeight: '98vh'
        }}
      >
        {/* Unified content area with rounded corners and background */}
        <div 
          className="relative w-full h-full bg-background dark:bg-dark-background rounded-t-[20px]"
        >
          {/* Drag handle overlaid on top of content */}
          <div 
            ref={handleRef}
            className="absolute top-0 left-0 right-0 z-[9999] h-8 flex items-center justify-center"
            style={{ backgroundColor: 'transparent' }}
          >
            <div className="w-12 h-1.5 bg-zinc-300 dark:bg-zinc-700 rounded-full"></div>
          </div>
          
          {/* Scrollable content that starts from the top */}
          <div 
            ref={contentRef}
            className="h-full overflow-y-auto pb-safe"
            style={{ touchAction: isDragging ? 'none' : 'auto' }}
          >
            {/* Add padding to the top to account for the drag handle */}
            <div className="pt-6">
              <FighterComparison />
            </div>

            {/* Fixed navigation buttons at the bottom */}
            <div className="fixed bottom-0 left-0 right-0 flex items-center justify-center space-x-10 p-8 bg-gradient-to-t from-background dark:from-dark-background to-transparent">
              <ClickableElement
                as="button"
                onClick={navigateToPrevFighter}
                disabled={selectedFightIndex <= 0}
                className="flex items-center justify-center w-12 h-12 rounded-full backdrop-blur-md bg-white/10 dark:bg-black/30 text-text-secondary dark:text-dark-text-secondary active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ 
                  backdropFilter: 'blur(8px)',
                  WebkitBackdropFilter: 'blur(8px)'
                }}
              >
                <CaretLeft className="h-6 w-6" weight="bold" />
              </ClickableElement>

              <ClickableElement
                as="button"
                onClick={onClose}
                className="flex items-center justify-center w-12 h-12 rounded-full backdrop-blur-md bg-white/10 dark:bg-black/30 text-text-secondary dark:text-dark-text-secondary active:scale-95"
                style={{ 
                  backdropFilter: 'blur(8px)',
                  WebkitBackdropFilter: 'blur(8px)'
                }}
              >
                <X className="h-6 w-6" weight="bold" />
              </ClickableElement>

              <ClickableElement
                as="button"
                onClick={navigateToNextFighter}
                disabled={selectedFightIndex >= fightPredictions.length - 1}
                className="flex items-center justify-center w-12 h-12 rounded-full backdrop-blur-md bg-white/10 dark:bg-black/30 text-text-secondary dark:text-dark-text-secondary active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ 
                  backdropFilter: 'blur(8px)',
                  WebkitBackdropFilter: 'blur(8px)'
                }}
              >
                <CaretRight className="h-6 w-6" weight="bold" />
              </ClickableElement>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  // Render using a portal to ensure it's at the root level
  return createPortal(modalContent, document.body);
};

export default FighterSheet; 